import { motion } from 'framer-motion';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Clock, MapPin, Tag, ArrowLeft, ArrowRight } from 'lucide-react';
import { blogPosts } from '../data/blogPosts';
import { DreadlockHatsArticle } from '../components/blog/DreadlockHatsArticle';
import { CustomCrochetArticle } from '../components/blog/CustomCrochetArticle';
import { HandmadeBeanieArticle } from '../components/blog/HandmadeBeanieArticle';
import { HandmadeBlanketArticle } from '../components/blog/HandmadeBlanketArticle';
import { CrochetGiftArticle } from '../components/blog/CrochetGiftArticle';
import { HandCrochetedGiftsArticle } from '../components/blog/HandCrochetedGiftsArticle';
import { HomemadeBlanketWoodlandArticle } from '../components/blog/HomemadeBlanketWoodlandArticle';

const articleComponents = {
  'dreadlock-hats-sacramento-ca': DreadlockHatsArticle,
  'custom-crochet-hats-sacramento': CustomCrochetArticle,
  'handmade-beanie-hats-california': HandmadeBeanieArticle,
  'handmade-blankets-sacramento-ca': HandmadeBlanketArticle,
  'crochet-gifts-sacramento': CrochetGiftArticle,
  'hand-crocheted-gifts-sacramento-ca': HandCrochetedGiftsArticle,
  'homemade-blankets-woodland-ca': HomemadeBlanketWoodlandArticle,
};

const BlogIndex = () => {
  return (
    <div data-testid="blog-index">
      <Helmet>
        <title>Blog | Protect Your Crown - Handcrafted Crocheted Hats Sacramento</title>
        <meta name="description" content="Guides and articles about handcrafted crocheted hats, dreadlock tams, beanies, and custom crochet from Protect Your Crown in Sacramento CA." />
      </Helmet>

      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-5xl sm:text-6xl font-[Playfair_Display] text-[#2A2626] tracking-tight mb-6"
          >
            Blog
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-lg text-[#5C5855] leading-relaxed"
          >
            Guides, tips, and stories about handcrafted crochet from Sacramento CA by Christian Clark
          </motion.p>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-5xl mx-auto px-6">
          <div className="space-y-8">
            {blogPosts.map((post, index) => (
              <motion.div
                key={post.slug}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Link
                  to={`/blog/${post.slug}`}
                  className="group block bg-white border border-[#EADCCC] rounded-lg overflow-hidden hover:shadow-lg transition-shadow"
                  data-testid={`blog-card-${post.slug}`}
                >
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-0">
                    <div className="aspect-[4/3] md:aspect-auto overflow-hidden">
                      <img
                        src={post.image}
                        alt={post.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="p-6 md:p-8 md:col-span-2 flex flex-col justify-center">
                      <div className="flex flex-wrap items-center gap-3 text-xs text-[#5C5855] mb-3">
                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{post.date}</span>
                        <span className="flex items-center gap-1"><Tag className="w-3 h-3" />{post.readTime}</span>
                      </div>
                      <h2 className="text-xl md:text-2xl font-[Playfair_Display] text-[#2A2626] group-hover:text-[#A85A43] transition-colors mb-3">
                        {post.title}
                      </h2>
                      <p className="text-[#5C5855] text-sm leading-relaxed mb-4">{post.excerpt}</p>
                      <span className="inline-flex items-center gap-2 text-[#A85A43] font-[Manrope] font-semibold text-sm group-hover:gap-3 transition-all">
                        Read Article <ArrowRight className="w-4 h-4" />
                      </span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

const BlogPost = ({ slug }) => {
  const post = blogPosts.find(p => p.slug === slug);
  const ArticleComponent = articleComponents[slug];

  if (!post || !ArticleComponent) {
    return (
      <div className="py-24 text-center">
        <h1 className="text-3xl font-[Playfair_Display] text-[#2A2626] mb-4">Article Not Found</h1>
        <Link to="/blog" className="text-[#A85A43] hover:text-[#8C4935] font-[Manrope] font-semibold">Back to Blog</Link>
      </div>
    );
  }

  const pageTitle = `${post.title} | Protect Your Crown Blog`;
  
  return (
    <div data-testid={`blog-post-${slug}`}>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={post.metaDescription || ''} />
        <meta name="keywords" content={post.keywords || ''} />
        <link rel="canonical" href={`https://protectyourcrown.store/blog/${post.slug}`} />
        <meta property="og:title" content={post.title || ''} />
        <meta property="og:description" content={post.metaDescription || ''} />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={post.image || ''} />
      </Helmet>

      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            "headline": post.title,
            "description": post.metaDescription,
            "author": { "@type": "Person", "name": "Christian Clark", "jobTitle": "Founder, Protect Your Crown" },
            "publisher": { "@type": "Organization", "name": "Protect Your Crown" },
            "datePublished": post.dateISO,
            "dateModified": post.dateISO,
            "image": post.image,
            "keywords": post.keywords,
            "mainEntityOfPage": `https://protectyourcrown.store/blog/${post.slug}`
          })}
        </script>
      </Helmet>

      <section className="py-8 bg-white border-b border-[#EADCCC]">
        <div className="max-w-4xl mx-auto px-6">
          <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-[#A85A43] hover:text-[#8C4935] font-[Manrope] transition-colors" data-testid="blog-back-link">
            <ArrowLeft className="w-4 h-4" />
            All Articles
          </Link>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          <ArticleComponent />
        </div>
      </section>

      {/* Related Articles */}
      <section className="py-16 bg-[#F9F6F0]">
        <div className="max-w-5xl mx-auto px-6">
          <h3 className="text-2xl font-[Playfair_Display] text-[#2A2626] mb-8 text-center">More Articles</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {blogPosts.filter(p => p.slug !== slug).slice(0, 2).map(related => (
              <Link
                key={related.slug}
                to={`/blog/${related.slug}`}
                className="group block bg-white border border-[#EADCCC] rounded-lg overflow-hidden hover:shadow-lg transition-shadow"
                data-testid={`related-${related.slug}`}
              >
                <div className="aspect-[16/9] overflow-hidden">
                  <img src={related.image} alt={related.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                </div>
                <div className="p-6">
                  <p className="text-xs text-[#5C5855] mb-2">{related.date}</p>
                  <h4 className="text-lg font-[Playfair_Display] text-[#2A2626] group-hover:text-[#A85A43] transition-colors">{related.title}</h4>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default function Blog() {
  const { slug } = useParams();

  if (slug) {
    return <BlogPost slug={slug} />;
  }

  return <BlogIndex />;
}
